import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  useGetServiceBulletinsQuery,
  useLazyGetChassisByBulletinQuery,
  useAddServiceBulletinToBasketMutation,
} from "../../../api/endpoints/basket";

const ServiceBulletinOrderModal = ({ visible, onHide }) => {
  const { th } = useLanguage();
  const toast = useRef(null);

  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [selectedChassis, setSelectedChassis] = useState([]);

  const { data: bulletinsResponse, isLoading: isLoadingBulletins } =
    useGetServiceBulletinsQuery(undefined, { skip: !visible });

  const [triggerGetChassis, { data: chassisResponse, isLoading: isLoadingChassis }] =
    useLazyGetChassisByBulletinQuery();

  const [addToBasket, { isLoading: isAddingToBasket }] =
    useAddServiceBulletinToBasketMutation();

  const bulletins = bulletinsResponse?.data || [];
  const chassisList = chassisResponse?.data?.chassisNumbers || [];

  const bulletinOptions = bulletins.map((item) => ({
    label: item.value,
    value: item.key,
  }));

  const chassisOptions = chassisList.map((chassis) => ({
    label: chassis,
    value: chassis,
  }));

  useEffect(() => {
    if (visible) {
      setSelectedBulletin(null);
      setSelectedChassis([]);
    }
  }, [visible]);

  useEffect(() => {
    if (selectedBulletin) {
      triggerGetChassis(selectedBulletin);
      setSelectedChassis([]);
    }
  }, [selectedBulletin, triggerGetChassis]);

  const handleCreateOrder = async () => {
    if (!selectedBulletin) {
      toast.current?.show({
        severity: "warn",
        summary: th("common.warning"),
        detail: th("basket.selectServiceBulletin"),
        life: 3000,
      });
      return;
    }

    if (!selectedChassis || selectedChassis.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: th("common.warning"),
        detail: th("basket.selectChassis"),
        life: 3000,
      });
      return;
    }

    try {
      const chassisNos = selectedChassis.map((ch) => ch.value || ch);
      await addToBasket({
        qmnum: selectedBulletin,
        chassisNos: chassisNos,
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: th("common.success"),
        detail: th("basket.serviceBulletinOrderCreated"),
        life: 3000,
      });

      onHide?.();
      window.location.href = "/basket";
    } catch (error) {
      const errorMessage =
        error?.data?.errorMessage || th("basket.serviceBulletinOrderError");
      toast.current?.show({
        severity: "error",
        summary: th("common.error"),
        detail: errorMessage,
        life: 3500,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={th("common.cancel")}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={onHide}
        disabled={isAddingToBasket}
      />
      <Button
        label={th("basket.createOrder")}
        icon="pi pi-check"
        onClick={handleCreateOrder}
        loading={isAddingToBasket}
        disabled={!selectedBulletin || !selectedChassis || selectedChassis.length === 0}
      />
    </div>
  );

  return (
    <Dialog
      header={th("basket.serviceBulletinOrder")}
      visible={visible}
      onHide={onHide}
      style={{ width: "50rem" }}
      modal
      footer={footer}
      className="p-fluid"
    >
      <Toast ref={toast} />

      <div className="flex flex-column gap-3">
        {/* Servis Bülten Seçimi */}
        <div className="field">
          <label className="font-bold block mb-2">
            {th("basket.selectServiceBulletin")}
          </label>
          {isLoadingBulletins ? (
            <div className="flex align-items-center gap-2">
              <ProgressSpinner style={{ width: "20px", height: "20px" }} />
              <span>{th("common.loading")}</span>
            </div>
          ) : (
            <Dropdown
              value={selectedBulletin}
              options={bulletinOptions}
              onChange={(e) => setSelectedBulletin(e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder={th("basket.selectServiceBulletinPlaceholder")}
              className="w-full"
              filter
              showClear
            />
          )}
        </div>

        {/* Şasi Seçimi */}
        {selectedBulletin && (
          <div className="field">
            <label className="font-bold block mb-2">
              {th("basket.selectChassis")}
            </label>
            {isLoadingChassis ? (
              <div className="flex align-items-center gap-2">
                <ProgressSpinner style={{ width: "20px", height: "20px" }} />
                <span>{th("common.loading")}</span>
              </div>
            ) : chassisList.length === 0 ? (
              <div className="p-2 border-1 surface-border border-round">
                <span className="text-color-secondary">
                  {th("basket.noChassisFound")}
                </span>
              </div>
            ) : (
              <>
                <MultiSelect
                  value={selectedChassis}
                  options={chassisOptions}
                  onChange={(e) => setSelectedChassis(e.value)}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={th("basket.selectChassisPlaceholder")}
                  className="w-full"
                  filter
                  display="chip"
                  showClear
                />
                {selectedChassis && selectedChassis.length > 0 && (
                  <div className="mt-2 p-2 border-1 surface-border border-round bg-primary-50">
                    <strong>{th("basket.affectedVehicleCount")}: </strong>
                    <span>{selectedChassis.length}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ServiceBulletinOrderModal;

